import React, { useState, useEffect } from "react";
import { StyleSheet, View, Text, Pressable, Modal } from "react-native";
import axios from "axios";
import { useUser } from "../../Widgets/UserContext";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore"; // Ensure you import these
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth"; // Ensure correct imports
import { app, auth } from "../../firebase"; // Assuming you have these firebase configurations	
import { stripeConfigService } from "../../services/StripeConfigService"; // Import Stripe config service
import { StripeTrustNotice, SetupSecondaryActions, ModalCloseLink } from "./GradFundSetupInfo";
import { SPOTLIGHT_TARGETS, spotlightTarget } from "../../Widgets/SpotlightTutorial/spotlightTargets";

let LOCAL_SERVER_URL = "http://localhost:5001/my-tassel/us-central1/api";
let FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";

// Always use production Firebase Functions (no emulators)
const SERVER_URL = FIREBASE_SERVER_URL;

const isStripeAccountReady = (account) => {
    const requirements = account?.requirements || {};
    const currentlyDue = requirements.currently_due || [];
    const pastDue = requirements.past_due || [];
    const pendingVerification = requirements.pending_verification || [];

    return Boolean(
        account?.details_submitted &&
        account?.charges_enabled &&
        account?.payouts_enabled &&
        currentlyDue.length === 0 &&
        pastDue.length === 0 &&
        pendingVerification.length === 0
    );
};


function ConnectedAccount(pageOwner) {
    const db = getFirestore(app);
    const email = pageOwner.email;
    const [accountCreated, setAccountCreated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { currentUser, currentUserData, setCurrentUser, setCurrentUserData } = useUser();

    const handleCreateAccount = async () => {
        setIsLoading(true);
        try {
            const returnUrl = pageOwner.pages.registry || pageOwner.pages.events;
            alert(`Attempting to use return URL: ${returnUrl}`); // Alert the URL being used

            if (!returnUrl) {
                throw new Error("No valid return URL found.");
            }

            const response = await axios.post(`${SERVER_URL}/create-connected-account`, {
                email: email,
                refresh_url: returnUrl,
                return_url: returnUrl,
                pageOwner: pageOwner,
                capabilities: {
                    transfers: { requested: true },
                    card_payments: { requested: true }
                }
            });

            // 
            // Redirect to Stripe's onboarding form
            if (response.data.accountLink && response.data.accountLink.url) {
                window.location = response.data.accountLink.url;
            } else {
                // Adjusted if response.data directly contains the URL
                window.location = response.data.url;
            }
        } catch (error) {
            
            alert(`Failed to create connected account: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
};

const ConnectedAccountModal = ({ visible, onClose, pageOwner }) => {
    const [email, setEmail] = useState(pageOwner?.email || '');
    const [isLoading, setIsLoading] = useState(false);
    const [hasStripeAccount, setHasStripeAccount] = useState(false);
    const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
    const [showFinishSetupButton, setShowFinishSetupButton] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
    const [currentStripeMode, setCurrentStripeMode] = useState('test'); // Default to test mode
    const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
    const [isHowItWorksExpanded, setIsHowItWorksExpanded] = useState(false);
    const db = getFirestore(app);
    const { currentUser, currentUserData } = useUser();

    // Check if current user is admin
    const isAdmin = currentUserData?.accountType === 'admin';

    // Load current Stripe mode on component mount
    useEffect(() => {
        const loadStripeMode = async () => {
            try {
                const mode = await stripeConfigService.getStripeMode();
                setCurrentStripeMode(mode);
                
            } catch (error) {
                
                setCurrentStripeMode('test'); // Fallback to test mode
            }
        };
        loadStripeMode();
    }, [isAdmin]);

    // Early return if pageOwner is null
    if (!pageOwner) {
        return (
            <Modal visible={visible} transparent={true} onRequestClose={onClose}>
                <View style={styles.blurredContainer}>
                    <View style={styles.modalContent}>
                        <Text style={styles.title}>Loading...</Text>
                        <Text style={styles.actionText}>Please wait while we load your account information.</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <Text style={styles.buttonText}>Close</Text>
                        </Pressable>
                    </View>
                </View>
            </Modal>
        );
    }

    // Check for cached stripeAccountId and Firestore status
    useEffect(() => {
        if (!pageOwner || !email) return;
        
        const cachedStripeId = localStorage.getItem(`stripeAccountId_${email}`);
        const hasFirestoreStripeId = pageOwner?.stripeAccountId;
        const hasCompletedSetup = Boolean(hasFirestoreStripeId && pageOwner?.stripeSetupComplete);

        console.log('ConnectedAccountModal Local Storage Debug:', {
            email,
            cachedStripeId,
            hasFirestoreStripeId,
            localStorageKey: `stripeAccountId_${email}`,
        });

        if ((cachedStripeId && !hasFirestoreStripeId) || (hasFirestoreStripeId && !hasCompletedSetup)) {
            setShowFinishSetupButton(true);
            setStatusMessage({
                type: 'info',
                message: 'Finish Stripe onboarding so your Grad Fund can start receiving payments.'
            });
        }
        setHasStripeAccount(hasCompletedSetup);
    }, [email, pageOwner]);

    const checkStripeAccountReadiness = async (accountId) => {
        const response = await axios.get(`${SERVER_URL}/account-status/${accountId}`, {
            params: {
                isTestMode: currentStripeMode === 'test',
                isAdmin,
            },
        });
        const account = response.data?.account;
        setStripeAccountStatus(account);
        return isStripeAccountReady(account);
    };

    const handleFinishSetup = async () => {
        setIsLoading(true);
        setStatusMessage({ type: '', message: '' });

        try {
            const userRef = doc(db, "users", email);
            const cachedStripeId = localStorage.getItem(`stripeAccountId_${email}`);
            const accountId = pageOwner?.stripeAccountId || cachedStripeId;

            if (!accountId) {
                setStatusMessage({
                    type: 'error',
                    message: 'No Stripe account was found. Please restart the setup process.'
                });
                return;
            }

            const isReady = await checkStripeAccountReadiness(accountId);

            if (!isReady) {
                await updateDoc(userRef, {
                    stripeAccountId: accountId,
                    stripeSetupComplete: false,
                    stripeAccountStatusLastCheckedAt: new Date().toISOString(),
                });
                setHasStripeAccount(false);
                setShowFinishSetupButton(true);
                setStatusMessage({
                    type: 'info',
                    message: 'Stripe still needs more information before your Grad Fund can receive payments. Complete any remaining Stripe onboarding steps, then return here.'
                });
                return;
            }

            await updateDoc(userRef, {
                stripeAccountId: accountId,
                stripeSetupComplete: true,
                stripeAccountStatusLastCheckedAt: new Date().toISOString(),
            });
            localStorage.removeItem(`stripeAccountId_${email}`);
            setHasStripeAccount(true);
            setShowFinishSetupButton(false);
            setStatusMessage({
                type: 'success',
                message: 'Stripe account setup completed successfully! Your Grad Fund is ready to receive payments.'
            });
            setTimeout(() => window.location.reload(), 2000);
        } catch (error) {
            setStatusMessage({
                type: 'error',
                message: 'Unable to verify your Stripe setup right now. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateAccount = async () => {
        setIsLoading(true);
        setStatusMessage({ type: '', message: '' });
        try {
            const returnUrl = pageOwner.pages.registry || pageOwner.pages.events;
            
            

            if (!returnUrl) {
                throw new Error("No valid return URL found.");
            }

            // Use global test mode setting - applies to all users when admin sets it to test
            const shouldUseTestMode = currentStripeMode === 'test';
            

            const response = await axios.post(`${SERVER_URL}/create-connected-account`, {
                email: email,
                refresh_url: returnUrl,
                return_url: returnUrl,
                pageOwner: pageOwner,
                isTestMode: shouldUseTestMode,
                isAdmin: isAdmin,
                capabilities: {
                    transfers: { requested: true },
                    card_payments: { requested: true }
                }
            });

            

            if (response.data.stripeAccountId) {
                console.log('Saving to localStorage:', {
                    key: `stripeAccountId_${email}`,
                    value: response.data.stripeAccountId
                });
                localStorage.setItem(`stripeAccountId_${email}`, response.data.stripeAccountId);
            }

            if (response.data.url) {
                window.location = response.data.url;
            } else {
                setStatusMessage({
                    type: 'error',
                    message: 'Failed to get the account link URL. Please try again.'
                });
            }
        } catch (error) {
            
            setStatusMessage({
                type: 'error',
                message: `Failed to create connected account: ${error.message}`
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRestartProcess = () => {
        localStorage.removeItem(`stripeAccountId_${email}`);
        setShowFinishSetupButton(false);
        setStatusMessage({
            type: 'info',
            message: 'Setup process restarted. You can begin again.'
        });
        setTimeout(() => handleCreateAccount(), 1000);
    };

    const handleContinueOnboarding = async () => {
        setIsLoading(true);
        setStatusMessage({ type: '', message: '' });

        try {
            const cachedStripeId = localStorage.getItem(`stripeAccountId_${email}`);
            const accountId = pageOwner?.stripeAccountId || cachedStripeId;
            const returnUrl = pageOwner.pages.registry || pageOwner.pages.events;

            if (!accountId) {
                await handleCreateAccount();
                return;
            }

            if (!returnUrl) {
                throw new Error("No valid return URL found.");
            }

            const response = await axios.post(`${SERVER_URL}/create-onboarding-link`, {
                accountId,
                refreshUrl: returnUrl,
                returnUrl,
                isTestMode: currentStripeMode === 'test',
                isAdmin,
            });

            if (response.data?.url) {
                window.location = response.data.url;
            } else {
                setStatusMessage({
                    type: 'error',
                    message: 'Failed to get the Stripe onboarding link. Please try again.'
                });
            }
        } catch (error) {
            setStatusMessage({
                type: 'error',
                message: `Failed to continue Stripe onboarding: ${error.message}`
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUnlinkStripeConfirm = async () => {
        setIsLoading(true);
        setStatusMessage({ type: '', message: '' });
        try {
            const userRef = doc(db, "users", email);
            await updateDoc(userRef, {
                stripeAccountId: null,
                stripeSetupComplete: false
            });
            localStorage.removeItem(`stripeAccountId_${email}`);
            setHasStripeAccount(false);
            setShowFinishSetupButton(false);
            setShowUnlinkConfirmation(false);
            setStatusMessage({
                type: 'success',
                message: 'Stripe account successfully unlinked'
            });
            setTimeout(() => window.location.reload(), 2000);
        } catch (error) {
            
            setStatusMessage({
                type: 'error',
                message: 'Failed to unlink Stripe account. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (!visible) return null;

    return (
        <Modal visible={visible} transparent={true} onRequestClose={onClose}>
            <View style={styles.blurredContainer}>
                <View style={styles.modalContent} {...spotlightTarget(SPOTLIGHT_TARGETS.GRADFUND_SETUP_MODAL)}>
                    <Text style={styles.title}>Graduation Fund Setup</Text>
                    
                    {!hasStripeAccount && <StripeTrustNotice />}

                    {statusMessage.message && (
                        <Text style={[
                            styles.statusMessage,
                            statusMessage.type === 'error' && styles.errorMessage,
                            statusMessage.type === 'success' && styles.successMessage,
                            statusMessage.type === 'info' && styles.infoMessage,
                        ]}>
                            {statusMessage.message}
                        </Text>
                    )}

                    {/* Admin mode indicator */}
                    {isAdmin && (
                        <View style={styles.adminModeIndicator}>
                            <Text style={styles.adminModeText}>
                                🔑 Admin Mode: Creating account in {currentStripeMode.toUpperCase()} mode
                            </Text>
                        </View>
                    )}

                    {!hasStripeAccount && !showFinishSetupButton && (
                        <View style={styles.actionContainer}>
                            <Text style={styles.actionText}>
                                Ready to activate your Grad Fund? Set up your Stripe account to start receiving Grad Fund payments.
                                {isAdmin && ` (${currentStripeMode.toUpperCase()} mode)`}
                            </Text>
                            <Pressable 
                                onPress={handleCreateAccount} 
                                disabled={isLoading} 
                                style={[styles.button, isLoading && styles.buttonDisabled]}
                            >
                                <Text style={styles.buttonText}>
                                    {isLoading ? 'Creating Account...' : 'Activate Your Grad Fund'}
                                </Text>
                            </Pressable>
                        </View>
                    )}

                    {showFinishSetupButton && (
                        <View style={styles.actionContainer}>
                            <Text style={styles.actionText}>
                                Your Grad Fund won't accept payments until Stripe confirms that your account can accept charges and payouts.
                            </Text>
                            {stripeAccountStatus && !isStripeAccountReady(stripeAccountStatus) && (
                                <Text style={styles.requirementsText}>
                                    Current status: Stripe onboarding is still incomplete.
                                </Text>
                            )}
                            <Pressable 
                                onPress={handleContinueOnboarding}
                                style={[styles.button, isLoading && styles.buttonDisabled]}
                                disabled={isLoading}
                            >
                                <Text style={styles.buttonText}>{isLoading ? 'Opening Stripe...' : 'Continue Stripe Onboarding'}</Text>
                            </Pressable>
                            <Pressable 
                                onPress={handleFinishSetup}
                                style={[styles.button, { backgroundColor: '#2f5138' }, isLoading && styles.buttonDisabled]}
                                disabled={isLoading}
                            >
                                <Text style={styles.buttonText}>{isLoading ? 'Checking Stripe...' : 'Check Setup Status'}</Text>
                            </Pressable>
                            <Pressable 
                                onPress={handleRestartProcess}
                                style={[styles.button, { backgroundColor: '#666', marginTop: 10 }, isLoading && styles.buttonDisabled]}
                                disabled={isLoading}
                            >
                                <Text style={styles.buttonText}>Restart Setup Process</Text>
                            </Pressable>
                        </View>
                    )}

                    {hasStripeAccount && !showUnlinkConfirmation && (
                        <View style={styles.actionContainer}>
                            <Text style={styles.successText}>
                                Your Grad Fund is active and ready to receive payments!
                            </Text>
                            <Pressable 
                                onPress={() => setShowUnlinkConfirmation(true)} 
                                style={styles.unlinkButton}
                            >
                                <Text style={styles.buttonText}>Unlink Stripe Account</Text>
                            </Pressable>
                        </View>
                    )}

                    {/* Unlink Confirmation Modal */}
                    {showUnlinkConfirmation && (
                        <View style={styles.confirmationContainer}>
                            <Text style={styles.confirmationTitle}>
                                Are you sure you want to unlink your Stripe account?
                            </Text>
                            <Text style={styles.confirmationText}>
                                This will prevent you from receiving any future Grad Fund payments, until you re-link your account.
                                This action cannot be undone.
                            </Text>
                            <View style={styles.confirmationButtons}>
                                <Pressable 
                                    onPress={handleUnlinkStripeConfirm}
                                    style={[styles.confirmButton, isLoading && styles.buttonDisabled]}
                                    disabled={isLoading}
                                >
                                    <Text style={styles.buttonText}>
                                        {isLoading ? 'Unlinking...' : 'Yes, Unlink Account'}
                                    </Text>
                                </Pressable>
                                <Pressable 
                                    onPress={() => setShowUnlinkConfirmation(false)}
                                    style={styles.cancelButton}
                                    disabled={isLoading}
                                >
                                    <Text style={styles.buttonText}>Cancel</Text>
                                </Pressable>
                            </View>
                        </View>
                    )}

                    {hasStripeAccount && !showUnlinkConfirmation && (
                        <ModalCloseLink label="Close" onPress={onClose} />
                    )}
                    {!hasStripeAccount && (
                        <SetupSecondaryActions
                            isHowItWorksExpanded={isHowItWorksExpanded}
                            onToggleHowItWorks={() => setIsHowItWorksExpanded((previous) => !previous)}
                            onSkip={onClose}
                        />
                    )}
                </View>
            </View>
        </Modal>
    );
};


const styles = StyleSheet.create({
    blurredContainer: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backdropFilter: "blur(10px)", // Apply blur effect
        justifyContent: "center",
        alignItems: "center",
    },
    modalContent: {
        padding: 30,
        backgroundColor: "#fefde2",
        borderRadius: 15,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
        margin: 20,
        alignItems: "center",
        maxWidth: 500,
        width: "90%",
        maxHeight: "90vh",
        overflow: "auto",
    },
    title: {
        fontSize: 24,
        fontWeight: "bold",
        marginBottom: 20,
    },
    button: {
        backgroundColor: "#4c956c",
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 25,
        width: "100%",
        maxWidth: 300,
        alignItems: "center",
        marginTop: 10,
    },
    buttonText: {
        color: "#fff",
        fontSize: 16,
        fontWeight: "600",
    },
    closeButton: {
        backgroundColor: "red",
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 20,
        marginTop: 10,
        width: 200, // Adjusts button width to match modal's width
        alignItems: "center", // Ensures text is centered within the button
    },
    unlinkButton: {
        backgroundColor: "#d32f2f",
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 25,
        width: "100%",
        maxWidth: 300,
        alignItems: "center",
        marginTop: 10,
    },
    statusMessage: {
        padding: 15,
        borderRadius: 8,
        marginBottom: 20,
        textAlign: "center",
        width: "100%",
        fontSize: 14,
    },
    errorMessage: {
        backgroundColor: '#ffebee',
        color: '#c62828',
    },
    successMessage: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
    },
    infoMessage: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
    },
    buttonDisabled: {
        opacity: 0.7,
    },
    confirmationContainer: {
        width: "100%",
        alignItems: "center",
        backgroundColor: "rgba(211, 47, 47, 0.1)",
        padding: 20,
        borderRadius: 10,
    },
    confirmationTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center',
        color: '#d32f2f',
    },
    confirmationText: {
        marginBottom: 20,
        textAlign: 'center',
        color: '#666',
    },
    confirmationButtons: {
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
    },
    confirmButton: {
        backgroundColor: '#d32f2f',
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 20,
        marginBottom: 10,
        width: 200,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#666',
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 20,
        width: 200,
        alignItems: 'center',
    },
    successText: {
        color: '#2e7d32',
        marginBottom: 15,
        textAlign: 'center',
    },
    actionContainer: {
        width: "100%",
        alignItems: "center",
        marginBottom: 12,
    },
    adminModeIndicator: {
        backgroundColor: '#e3f2fd',
        padding: 12,
        borderRadius: 8,
        marginBottom: 15,
        borderLeftWidth: 4,
        borderLeftColor: '#2196f3',
    },
    adminModeText: {
        fontSize: 14,
        color: '#1976d2',
        fontWeight: '600',
        textAlign: 'center',
    },
    actionText: {
        fontSize: 16,
        color: "#666",
        marginBottom: 15,
        textAlign: "center",
        lineHeight: 22,
    },
    requirementsText: {
        fontSize: 14,
        color: "#c77d1a",
        marginBottom: 12,
        textAlign: "center",
        fontWeight: "600",
    },
});

export { ConnectedAccount, ConnectedAccountModal };